import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getJobDocuments, getJobDocumentsByTagLevels, getJobDocumentsTagLevel } from "../../docuclipper/api";
import { JobDocument } from "../../docuclipper/DocuclipperTypes";
import { setSelectedDocumentId } from "./TransactionManager";
import { ReduxState } from "..";
import { createAlert } from "./Alerts";

const initialState: {
  data: JobDocument[] | null;
  loading: boolean;
  error: string | null;
} = {
  data: null,
  loading: false,
  error: null,
};

const slice = createSlice({
  name: "JobDocument",
  initialState,
  reducers: {
    load(state) {
      state.loading = true;
      state.error = null;
    },
    setData(state, action: PayloadAction<JobDocument[]>) {
      state.loading = false;
      state.data = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const { load, setError, setData } = slice.actions;

export const fetchJobDocuments = ({
  fieldName
}: {
  fieldName?: 'bankMode' | 'invoiceMode' | 'formMode' | 'generic' | 'template' | 'brokerageMode'
}) => async (dispatch, getState) => {
  const state: ReduxState = getState();

  const { loading } = state.JobDocument;
  if (loading) {
    return;
  }
  const jobId = state.JobData.job?.id;

  const { selectedL1: l1, selectedL2: l2 } = state.TagLevel;
  if (!l1 && !l2 && !jobId) {
    return;
  }

  dispatch(load());
  try {
    let data: JobDocument[] = [];
    if (l1 && l2) {
      const resolvedFieldName = fieldName ?? "bankMode";
      data = await getJobDocumentsTagLevel(l1, l2, resolvedFieldName as 'bankMode' | 'invoiceMode' | 'formMode' | 'generic' | 'template' | 'brokerageMode');
    } else if (jobId) {
      data = await getJobDocuments(jobId);
    }

    dispatch(setData(data));
    if (data.length > 0) {
      // Get newDocuments from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const newDocuments = urlParams.get('newDocuments');

      if (newDocuments) {
        // Split the comma-separated IDs and find first matching document
        const documentIds = newDocuments.split(',').map(Number);
        const matchingDocument = data.find(doc => documentIds.includes(Number(doc.documentId)));

        if (matchingDocument) {
          dispatch(setSelectedDocumentId(matchingDocument.documentId));
        } else {
          // Fallback to first document if no match found
          dispatch(setSelectedDocumentId(data[0].documentId));
        }
      } else {
        // No newDocuments param, use first document
        dispatch(setSelectedDocumentId(data[0].documentId));
      }
    }
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : "Error getting job documents";
    dispatch(setError(errorMessage));
    dispatch(createAlert({
      message: (error as any)?.message || 'Error getting documents',
      timeout: 0,
      type: 'error'
    }))
  }
};

export default slice.reducer;
